/**
 * Publisher API client (Safari/iOS-safe).
 *
 * Why this file exists:
 * Apple Mail Privacy Protection + Safari ITP frequently *drop* `Set-Cookie`
 * headers when the user clicks a magic link from the Mail app — the cookie
 * never reaches `/publisher/dashboard`, so the dashboard sees "not logged
 * in" and bounces the user back to /publisher/login. We saw this exact
 * symptom on production.
 *
 * Fix: store the session token in `localStorage` (which Safari preserves
 * reliably across context switches) and send it as an `Authorization:
 * Bearer …` header. We also keep `withCredentials: true` so the legacy
 * cookie path still works on desktop browsers as a fallback.
 *
 * The token is written here by the magic-link callback page and consumed by
 * every publisher-scoped API call. `clearPublisherToken()` is called on
 * explicit logout.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLISHER_TOKEN_KEY = "hac_publisher_token";

export const getPublisherToken = () =>
  (typeof window !== "undefined" ? localStorage.getItem(PUBLISHER_TOKEN_KEY) : null);

export const setPublisherToken = (token) => {
  if (typeof window === "undefined") return;
  if (token) localStorage.setItem(PUBLISHER_TOKEN_KEY, token);
  else localStorage.removeItem(PUBLISHER_TOKEN_KEY);
};

export const clearPublisherToken = () => setPublisherToken(null);

export const publisherClient = axios.create({
  baseURL: API,
  withCredentials: true, // desktop fallback (HttpOnly cookie still set by backend)
  timeout: 30000,
});

publisherClient.interceptors.request.use((cfg) => {
  const t = getPublisherToken();
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }
  return cfg;
});

// On 401 from a publisher endpoint, clear the stale token so the UI cleanly
// re-renders into the "not logged in" state instead of looping.
publisherClient.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      clearPublisherToken();
    }
    return Promise.reject(err);
  },
);



// ===== Publisher media + content helpers (Bearer-authed) =====
export const publisherListMedia = (slug) =>
  publisherClient.get(`/publisher/apps/${slug}/media`).then((r) => r.data);

export const publisherUploadImage = (slug, file) => {
  const fd = new FormData();
  fd.append("file", file);
  return publisherClient
    .post(`/publisher/apps/${slug}/uploads`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    })
    .then((r) => r.data);
};

export const publisherCreateMedia = (slug, payload) =>
  publisherClient.post(`/publisher/apps/${slug}/media`, payload).then((r) => r.data);

export const publisherUpdateMedia = (slug, id, payload) =>
  publisherClient.patch(`/publisher/apps/${slug}/media/${id}`, payload).then((r) => r.data);

export const publisherDeleteMedia = (slug, id) =>
  publisherClient.delete(`/publisher/apps/${slug}/media/${id}`).then((r) => r.data);

export const publisherReorderMedia = (slug, ids) =>
  publisherClient.post(`/publisher/apps/${slug}/media/reorder`, { ids }).then((r) => r.data);
